"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "@/lib/auth-react";
import Link from "next/link";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Settings, LogOut, User, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import Navbar from "./Navbar";
import UserActions from "./UserActions";
import HeaderLogo from "./HeaderLogo";
import { DashboardMobileNav } from "./DashboardMobileNav";
import { UserAvatar } from "@/components/UserAvatar";
import {
  ImpersonationPill,
  getRecentAccounts,
  setRecentAccounts,
  type RecentAccount,
} from "@/components/dashboard/ImpersonationBanner";
import { StudentCreditBubble } from "@/components/layout/StudentCreditBubble";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { startImpersonation } from "@/utils/impersonate";
import { authFetch } from "@/utils/auth-fetch";
import { useLogout } from "@/hooks/useLogout";
import { toast } from "@/lib/notify";

const ROLE_BADGE_STYLES: Record<string, string> = {
  STUDENT: "bg-[#27335D] text-white",
  TEACHER: "bg-[#E8B54B] text-[#27335D]",
  ADMIN: "bg-[#B53135] text-white",
};

export default function Header({
  isDashboardRoute,
}: {
  isDashboardRoute?: boolean;
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const { data: session } = useSession();
  const { handleLogout, isLoggingOut } = useLogout();

  // Close mobile menu when pathname changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const userRole = session?.user?.role;

  // Recent impersonation targets (max 3), surfaced under the Impersonate
  // menu item. The store is client-side (localStorage) and can reference
  // accounts that have since been deleted — verify each still exists and
  // prune the rest so we never offer to impersonate a ghost.
  const [recentImpersonations, setRecentImpersonations] = useState<
    RecentAccount[]
  >([]);
  const accessToken = session?.accessToken;
  const currentUserId = session?.user?.id;
  useEffect(() => {
    if (userRole !== "ADMIN" || !accessToken) {
      setRecentImpersonations([]);
      return;
    }
    let cancelled = false;
    const stored = getRecentAccounts().filter((a) => a.id !== currentUserId);
    if (stored.length === 0) {
      setRecentImpersonations([]);
      return;
    }
    Promise.all(
      stored.map((a) =>
        authFetch(`/api/users/${a.id}`, accessToken)
          .then((r) => (r.ok ? a : null))
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const existing = results.filter((a): a is RecentAccount => a !== null);
      setRecentImpersonations(existing);
      // Drop the deleted ones from the store so they don't come back.
      if (existing.length !== stored.length) setRecentAccounts(existing);
    });
    return () => {
      cancelled = true;
    };
  }, [userRole, accessToken, currentUserId]);

  const handleImpersonate = async (account: RecentAccount) => {
    try {
      await startImpersonation(account.id);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Impersonation failed",
      );
    }
  };

  /**
   * Format the signed-in user's name + email as a plain-text block
   * and write it to the clipboard. Used for the dropdown header
   * click-to-copy affordance.
   */
  const copyUserInfo = async () => {
    if (!session?.user) return;
    const roleLabel =
      userRole === "TEACHER"
        ? "Teacher"
        : userRole === "STUDENT"
          ? "Student"
          : userRole === "ADMIN"
            ? "Admin"
            : "User";
    const text = `${roleLabel} name: ${session.user.name ?? "—"}\nEmail: ${session.user.email ?? "—"}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied your info to clipboard", { duration: 3000 });
    } catch {
      toast.error("Couldn't copy — your browser blocked clipboard access");
    }
  };

  return (
    <header className="sticky top-[var(--app-banner-h,0px)] z-50 h-[4.5rem] w-full border-b-2 border-[#FFFFFF] bg-[#27335d]">
      <nav
        aria-label="Global"
        className={`flex h-full w-full items-center justify-between bg-[#27335d] px-4 ${
          isDashboardRoute ? "lg:pl-6 lg:pr-6 xl:pr-10" : "xl:px-24"
        }`}
      >
        {/* Logo Section */}
        <div className="flex items-center">
          <HeaderLogo />
        </div>
        {/* Desktop Navigation Links */}
        {!isDashboardRoute && (
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            <Navbar />
          </div>
        )}
        {/* User Actions — public pages AND user is NOT signed in */}
        {!isDashboardRoute && !session?.user && (
          <div className="hidden lg:flex lg:items-center">
            <UserActions />
          </div>
        )}
        {/* Impersonation pill — inline in navbar (admin impersonating) */}
        {session?.user && <ImpersonationPill />}
        {/* Desktop User Menu — shown whenever user is signed in (both
            dashboard and public routes) so it never differs. */}
        {session?.user && (
          <div
            className="hidden items-center gap-3 lg:flex"
            data-testid="desktopUserMenu"
          >
            <StudentCreditBubble variant="desktop" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-3 rounded-full border border-white/20 bg-white/5 py-2 pl-2 pr-5 text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
                  data-testid="desktopUserMenuButton"
                >
                  <UserAvatar
                    name={session.user.name}
                    src={session.user.image}
                    className="h-10 w-10 text-base"
                  />
                  <span className="flex flex-col items-start leading-tight">
                    <span className="max-w-[10rem] truncate text-base font-medium">
                      {session.user.name ?? "Account"}
                    </span>
                    {userRole ? (
                      <span
                        className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          ROLE_BADGE_STYLES[userRole] ?? "bg-white/20 text-white"
                        }`}
                      >
                        {userRole}
                      </span>
                    ) : null}
                  </span>
                  <svg
                    className="ml-1 h-5 w-5 transition-transform group-data-[state=open]:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 overflow-hidden rounded-xl border border-white/10 bg-[#1c2746] p-0 shadow-xl">
                <DropdownMenuLabel className="border-b border-white/10 p-0 font-normal">
                  <button
                    type="button"
                    onClick={(e) => {
                      // Keep the menu open so the user gets click feedback
                      // (toast) without it snapping shut on them.
                      e.preventDefault();
                      void copyUserInfo();
                    }}
                    data-testid="desktopUserMenuCopyInfo"
                    className="block w-full cursor-pointer px-4 py-3 text-left transition-colors hover:bg-white/5"
                    title="Click to copy your name + email"
                  >
                    <p className="truncate text-sm font-medium text-white">
                      {session.user.name}
                    </p>
                    <p className="truncate text-xs text-white/50">
                      {session.user.email}
                    </p>
                  </button>
                </DropdownMenuLabel>
                <div className="py-1">
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-4 py-2.5 text-sm text-white/80 focus:bg-white/10 focus:text-white">
                    <Link href="/dashboard" data-testid="desktopUserMenuDashboard">
                      <User className="h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-4 py-2.5 text-sm text-white/80 focus:bg-white/10 focus:text-white">
                    <Link href="/dashboard/settings" data-testid="desktopUserMenuSettings">
                      <Settings className="h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  {userRole === "ADMIN" && (
                    <DropdownMenuItem asChild className="cursor-pointer gap-3 px-4 py-2.5 text-sm text-white/80 focus:bg-white/10 focus:text-white">
                      <Link href="/dashboard/users" data-testid="desktopUserMenuImpersonate">
                        <Users className="h-4 w-4" />
                        Impersonate
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {userRole === "ADMIN" && recentImpersonations.length > 0 && (
                    <div
                      className="border-t border-white/5 pt-1"
                      data-testid="desktopUserMenuRecentImpersonations"
                    >
                      <p className="px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                        Recent
                      </p>
                      {recentImpersonations.map((account) => (
                        <DropdownMenuItem
                          key={account.id}
                          onSelect={() => handleImpersonate(account)}
                          data-testid={`desktopUserMenuRecentImpersonation-${account.id}`}
                          className="cursor-pointer gap-3 px-4 py-2 text-sm text-white/80 focus:bg-white/10 focus:text-white"
                        >
                          <UserAvatar
                            name={account.name}
                            src={account.image}
                            className="h-5 w-5 text-[10px]"
                          />
                          <span className="truncate">{account.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  )}
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <div className="px-3 py-2">
                  <LanguageSwitcher tone="dark" />
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <div className="py-1">
                  <DropdownMenuItem
                    onClick={() => handleLogout()}
                    disabled={isLoggingOut}
                    className="cursor-pointer gap-3 px-4 py-2.5 text-sm text-white/80 focus:bg-white/10 focus:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="desktopUserMenuLogout"
                  >
                    <LogOut className="h-4 w-4" />
                    {isLoggingOut ? "Logging out..." : "Log out"}
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {/* Mobile Menu Button — shows the whoami pill whenever signed in
            (dashboard or public), or the "Sign in" hamburger when signed out. */}
        <div className="flex items-center gap-2 lg:hidden">
          {session?.user ? (
            <>
              <StudentCreditBubble variant="mobile" />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 py-1 pl-1 pr-3 text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
              data-testid="whoamiPill"
              aria-label="Open menu"
            >
              <span className="sr-only">Toggle menu</span>
              <UserAvatar
                name={session.user.name}
                src={session.user.image}
                className="h-8 w-8"
              />
              <span className="flex flex-col items-start leading-tight">
                <span className="max-w-[7rem] truncate text-sm font-medium">
                  {session.user.name ?? "Account"}
                </span>
                {userRole ? (
                  <span
                    data-testid="whoamiRole"
                    className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                      ROLE_BADGE_STYLES[userRole] ?? "bg-white/20 text-white"
                    }`}
                  >
                    {userRole}
                  </span>
                ) : null}
              </span>
              {mobileMenuOpen ? (
                <XMarkIcon aria-hidden="true" className="ml-1 h-5 w-5" />
              ) : (
                <Bars3Icon aria-hidden="true" className="ml-1 h-5 w-5" />
              )}
            </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              {/* Primary action: Sign in. Direct link, doesn't open the menu. */}
              <Link
                href="/auth"
                data-testid="headerMobileSignInButton"
                className="rounded-full bg-[#F6C65A] px-4 py-1.5 text-sm font-semibold text-[#27335D] transition-colors hover:bg-[#e8b54b] focus:outline-none focus:ring-2 focus:ring-[#F6C65A]/40"
              >
                Sign in
              </Link>
              {/* Hamburger: just the menu icon now, no text. */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="cursor-pointer inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/30 text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
                data-testid="headerMobileMenuButton"
                aria-label="Open menu"
              >
                <span className="sr-only">Toggle menu</span>
                {mobileMenuOpen ? (
                  <XMarkIcon aria-hidden="true" className="h-5 w-5" />
                ) : (
                  <Bars3Icon aria-hidden="true" className="h-5 w-5" />
                )}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-full max-w-xs border-l-0 bg-[#27335d] p-6 text-white [&>button:last-child]:hidden">
          <div className="flex flex-row items-center justify-end">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="cursor-pointer rounded-md p-2.5 focus:ring-2 focus:ring-white focus:outline-none focus:ring-inset"
              data-testid="headerMobileMenuCloseButton"
            >
              <div className="flex flex-row items-center justify-center gap-1 rounded-xl bg-[#B53135] p-2 text-white">
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                <span>Close</span>
              </div>
            </button>
          </div>
          <div className="mt-10">
            {isDashboardRoute && userRole ? (
              <DashboardMobileNav
                role={userRole}
                onNavigate={() => setMobileMenuOpen(false)}
              />
            ) : (
              <>
                {/* Vertical Navigation Links for Mobile */}
                <nav className="space-y-4">
                  <Navbar vertical />
                </nav>
                {/* User Actions for Mobile */}
                <div className="mt-6">
                  <UserActions
                    vertical
                    setMobileMenuOpen={setMobileMenuOpen}
                  />
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}
